import { useSuspenseQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import React from 'react';

import * as api from '@gitbook/api-client';
import {
    DataTable,
    type DataTableColumns,
    DataTableFooter,
    DataTableLayout,
    Description,
    Dialog,
    DialogBody,
    DialogHeader,
    Heading,
    LoadingIndicator,
    LoadingPanel,
    Pagination,
} from '@gitbook/spine-react';

import { UserOverview } from '../../../Components';
import { NavigationLink } from '../../../navigation';
import {
    apiQuery,
    apiResource,
    useSiteContext,
    useSuspensePaginatedAPIQuery,
} from '../../../state';

const MAX_MEMBERS_PER_PAGE = 6;

const tableColumns: DataTableColumns<api.UserSitePermission> = [
    {
        key: 'user',
        label: 'User details',
        isRowHeader: true,
        cell: ({ item }) => <UserOverview user={item.user} details={item.user.email || '-'} />,
    },
    {
        key: 'permission',
        label: 'Permission level',
        alignment: 'end',
        cell: ({ item }) =>
            item.permission ? (
                <MemberPermissions permission={item.permission} origin={item.origin} />
            ) : null,
    },
];

const isAdminOrCreate = (permission: api.MemberRole) => {
    return permission === api.MemberRole.Admin || permission === api.MemberRole.Create;
};

function isSpacePointer(
    origin: api.SpacePointer | api.OrganizationPointer
): origin is api.SpacePointer {
    return (origin as api.SpacePointer).space !== undefined;
}

interface MembersAccessResponse {
    permission: api.MemberRole;
    user: api.User;
    origin: api.SpacePointer | api.OrganizationPointer;
}

export function MembersAccessDialog() {
    return (
        <Dialog>
            <DialogHeader>
                <Heading>Members with access</Heading>
                <Description>
                    Site members automatically inherit permissions from the space used as content in
                    the site. You can manage permissions in the space settings.
                </Description>
            </DialogHeader>
            <DialogBody>
                <React.Suspense fallback={<LoadingPanel />}>
                    <MembersTable />
                </React.Suspense>
            </DialogBody>
        </Dialog>
    );
}

function MembersTable() {
    const { organizationId, site } = useSiteContext();

    const page = useSuspensePaginatedAPIQuery(
        apiResource('listPermissionsAggregateInSite', {
            organizationId,
            siteId: site.id,
            limit: MAX_MEMBERS_PER_PAGE,
        })
    );

    const data = page.data.items || [];

    const tableKeyExtractor = (row: MembersAccessResponse) => row.user.id;

    return (
        <DataTableLayout>
            <DataTable
                aria-label="Members with access"
                kind="minimal"
                items={data}
                columns={tableColumns}
                getKey={tableKeyExtractor}
            />
            <DataTableFooter>
                <Pagination next={page.next} previous={page.previous} />
            </DataTableFooter>
        </DataTableLayout>
    );
}

function MemberPermissions({
    permission,
    origin,
}: {
    permission: api.MemberRole;
    origin: api.SpacePointer | api.OrganizationPointer;
}): React.ReactElement {
    return (
        <div className="flex flex-col items-end">
            <div
                className={clsx(
                    'font-medium',
                    isAdminOrCreate(permission) && 'text-primary text-ui-action'
                )}
            >
                {isAdminOrCreate(permission) ? 'Full access' : 'Read-only'}
            </div>
            {isSpacePointer(origin) ? (
                <SpaceLink origin={origin} />
            ) : (
                <div className="text-muted">Added to org</div>
            )}
        </div>
    );
}
function SpaceLink({ origin }: { origin: api.SpacePointer }): React.ReactElement {
    const { data: space } = useSuspenseQuery(apiQuery('getSpaceById', { spaceId: origin.space }));

    return (
        <React.Suspense fallback={<LoadingIndicator />}>
            <div className="flex items-center gap-1 text-muted">
                <div>Set in</div>
                <NavigationLink
                    to={{ screen: { type: 'space', space: origin.space } }}
                    className="max-w-36 truncate"
                >
                    {space.title}
                </NavigationLink>
            </div>
        </React.Suspense>
    );
}
